import { PopoverForOverflownText, Tooltip, icons } from '@lifebit-ai/lds'
import { DownloadFileMethods } from 'enums'
import React from 'react'

import DownloadResultFile from 'modules/resultsV2/DownloadResultFile'

import * as styles from './FileDetails.styles'

interface DownloadDetails {
  bucket: string
  fileKey: string
  fileName: string
  resultId: string
}

interface FileDetailsProps {
  displayName?: string
  /** Ignored when `downloadAction` is set: both render into the same slot and `downloadAction` wins. */
  downloadDetails?: DownloadDetails
  /** Download control to render instead of the bucket-based one, for files with a dedicated download endpoint. */
  downloadAction?: React.ReactNode
  iconTestId?: string
  isFile: boolean
  copyLinkTooltip?: string
  onCopyS3Link?: () => void
  onPreviewFile?: () => void
  subtitle?: React.ReactNode
  /** Native tooltip for truncated subtitles when `subtitle` is not a plain string. */
  subtitleTitle?: string
  hasBorder?: boolean
  /** Shown in the actions slot while file actions are still resolving. Real actions are omitted. */
  actionsPlaceholder?: React.ReactNode
}

const FileDetails = ({
  displayName,
  downloadDetails,
  downloadAction,
  hasBorder = true,
  iconTestId,
  isFile,
  copyLinkTooltip = 'Copy S3 link',
  onCopyS3Link,
  onPreviewFile,
  subtitle,
  subtitleTitle,
  actionsPlaceholder
}: FileDetailsProps): JSX.Element => {
  const resolvedSubtitleTitle = subtitleTitle ?? (typeof subtitle === 'string' ? subtitle : undefined)
  const hasActions = !!actionsPlaceholder || !!onPreviewFile || !!onCopyS3Link || !!downloadDetails || !!downloadAction

  return (
    <styles.ItemRow $hasBorder={hasBorder}>
      <styles.ItemIcon $isFile={isFile} data-testid={iconTestId ?? (isFile ? 'file-icon' : 'folder-icon')}>
        {isFile ? <icons.File /> : <icons.Folder />}
      </styles.ItemIcon>
      <styles.ItemInfo>
        {displayName && (
          <styles.ItemTitle>
            <PopoverForOverflownText title={displayName}>{displayName}</PopoverForOverflownText>
          </styles.ItemTitle>
        )}
        {subtitle && <styles.ItemSubtitle title={resolvedSubtitleTitle}>{subtitle}</styles.ItemSubtitle>}
      </styles.ItemInfo>
      {hasActions && (
        <styles.ItemActions>
          {actionsPlaceholder}
          {!actionsPlaceholder && onPreviewFile && (
            <Tooltip title="Preview">
              <styles.IconButton data-testid="preview-action" onClick={onPreviewFile}>
                <icons.EyeSmall />
              </styles.IconButton>
            </Tooltip>
          )}
          {!actionsPlaceholder && onCopyS3Link && (
            <Tooltip title={copyLinkTooltip}>
              <styles.IconButton data-testid="copy-s3-link-action" onClick={onCopyS3Link}>
                <icons.LinkSmall />
              </styles.IconButton>
            </Tooltip>
          )}
          {!actionsPlaceholder && downloadAction}
          {!actionsPlaceholder && !downloadAction && downloadDetails && (
            <DownloadResultFile {...downloadDetails} method={DownloadFileMethods.DownloadDirectlyWithHiddenLink} />
          )}
        </styles.ItemActions>
      )}
    </styles.ItemRow>
  )
}

export default FileDetails
